import {useState} from 'react';
import {Box, Typography as MuiTypo, styled, useTheme} from '@mui/material';
import Avatar from '@jobseeker/lotus/components/Avatar';
import LinearProgressWithLabel from '@jobseeker/lotus/elements/ProgressBar';
import Accordion, {AccordionSummary} from '@jobseeker/lotus/elements/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import {FaChevronDown} from 'react-icons/fa';
import {Image} from '@jobseeker/lotus/components/Image';
import {CheckOrange} from '@jobseeker/lotus/icons';
import ProfileCard from '@jobseeker/lotus/components/ProfileCard';
import {useSelector} from 'react-redux';
import type {AppState} from '@jobseeker/common-core/types';

import type {UserProfileCardProps} from '../type';

const LinkSection = styled(Box)(props => ({
  '.sectionLink': {
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: '1px solid #f0f0f0',
  },
  '.sectionLink:last-child': {
    border: 'none',
  },
}));

const Typography = styled(MuiTypo)<any>((props: any) => ({
  lineHeight: 'normal',
  fontWeight: props.bold && 600,
  margin: '0 0 5px',
}));

const PLAN_INFO = [
  {
    title: 'Looking for a Perfect Resume',
    description: 'Create one with hirist Resume Builder',
    link: '/subscribe/plans?scrollTo=resumeBuilder',
    planCode: 'hirist-RB',
    display: 'none',
  },
  {
    title: 'Be an early applicant on jobs',
    description: 'Get notified as soon as a relevant job is posted',
    link: '/subscribe/plans?scrollTo=earlyApplicant',
    planCode: 'hirist-EA',
    display: 'none',
  },
];

const Component = (props: UserProfileCardProps) => {
  const theme = useTheme();
  const [expand, setExpand] = useState(false);

  const toggleAcordion = () => {
    setExpand(prev => !prev);
  };
  const {profileStatus} = useSelector(
    (state: AppState) => state.user.completeness
  );

  const ABS_URL = 'https://static.iimjobs.com/';

  return (
    <Accordion
      expanded={expand}
      sx={{
        marginTop: !props?.showSmallBadges ? 0 : props?.isMyProfilePage ? 0 : theme.spacing(2),
        width: '100%',
        borderRadius: 2,
        p: props?.isMyProfilePage ? 0 : 2.5,
        mb: props?.isMyProfilePage ? 0 : 3.75,
        boxShadow: props?.isMyProfilePage
          ? 'none'
          : '0px 6px 6px 2px rgba(41, 41, 41, 0.02)',
        backgroundColor: props?.isMyProfilePage ? 'transparent' : '',
      }}
    >
      <AccordionSummary
        expandIcon={
          <FaChevronDown
            cursor="pointer"
            onClick={toggleAcordion}
            color={props?.isMyProfilePage ? '#4b4b4b' : '#212529'}
          />
        }
        aria-controls="panel1a-content"
        id="panel1a-header"
        sx={{
          alignItems: 'baseline',
          cursor: 'text !important',
          '.MuiAccordionSummary-content': {
            margin: !props?.showSmallBadges ? '12px 0' : '5px 0',
          },
          ...(props?.isMyProfilePage && {justifyContent: 'space-between'}),
        }}
      >
        {!props?.showSmallBadges ? (
          <ProfileCard {...props} />
        ) : (
          <Typography
            sx={{
              background: `url(https://static.iimjobs.com/resources/img/profstatbg.png) 16px 9px no-repeat !important`,
              cursor: 'pointer',
              fontSize: '14px',
              color: props?.isMyProfilePage ? '#4b4b4b' : 'gray.dark',
              padding: '10px 0 10px 50px!important',
            }}
          >
            Profile Status
          </Typography>
        )}
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: '0 !important',
        }}
      >
        <LinkSection>
          {props.showSmallBadges && (
            <Box
              sx={{
                display: 'flex',
                gap: theme.spacing(2),
                paddingBottom: theme.spacing(1),
              }}
            >
              <Box>
                <Avatar
                  imgUrl={props.profileImgUrl}
                  isOnline
                  sx={{width: 55, height: 55}}
                />
              </Box>
              <Box width="100%">
                <Typography
                  bold
                  component={'p'}
                  variant="subtitle1"
                  fontSize={props?.isMyProfilePage ? 13 : 16}
                  fontWeight={props?.isMyProfilePage ? '400 !important' : 500}
                  color={props?.isMyProfilePage ? '#4b4b4b' : 'gray.dark'}
                >
                  {props.name}
                </Typography>
                <Typography
                  component={'p'}
                  variant="subtitle2"
                  fontSize={props?.isMyProfilePage ? 12 : 14}
                  fontWeight={props?.isMyProfilePage ? '400 !important' : 500}
                  color={props?.isMyProfilePage ? '#4b4b4b' : 'gray.dark'}
                >
                  {props.email}
                </Typography>
                <LinearProgressWithLabel
                  value={props.profileProgress ?? 0}
                  isMyProfilePage={props?.isMyProfilePage}
                />
              </Box>
            </Box>
          )}
          {props.dropDownLinks?.map((link, i) => (
            <Box
              className="sectionLink"
              key={`userprofilelink${i}`}
              sx={{
                borderBottom: `1px solid ${
                  props?.isMyProfilePage ? '#e4e4e4' : '#f0f0f0'
                }`,
              }}
            >
              <MuiTypo
                fontSize={props?.isMyProfilePage ? 13 : 16}
                color={props?.isMyProfilePage ? '#888888' : ''}
                marginBlockStart={props?.isMyProfilePage ? 1 : '0.9em'}
                marginBlockEnd={props?.isMyProfilePage ? 1 : '0.9em'}
                marginInlineStart={0}
              >
                {link.label}
              </MuiTypo>

              <Box mt={props?.isMyProfilePage ? 1 : 2.5}>
                {link.isCompleted ? (
                  <Image
                    src={
                      props?.isMyProfilePage
                        ? `${ABS_URL}resources/img/tickigrey.png`
                        : CheckOrange
                    }
                    alt={'Completed'}
                    width={14}
                    height={props?.isMyProfilePage ? 10 : 14}
                    objectFit="cover"
                  />
                ) : (
                  <MuiTypo
                    fontSize={props?.isMyProfilePage ? 11 : 14}
                    color={props?.isMyProfilePage ? '#3c3c3c' : '#0057ff'}
                    component="a"
                    href={link.url}
                    target="_blank"
                    sx={{
                      cursor: 'pointer',
                    }}
                  >
                    {props?.isMyProfilePage ? 'Pending' : 'Update'}
                  </MuiTypo>
                )}
              </Box>
            </Box>
          ))}
        </LinkSection>
      </AccordionDetails>
    </Accordion>
  );
};

export default Component;
